import React, { useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaFacebookF, FaInstagram, FaLinkedinIn } from "react-icons/fa";
import {
  t as translate,
  normalizeLang,
  getLangFromPathname,
} from "../../i18n";
import Logo from "../../assets/logo.png";
import { useSiteContent } from "../../content/SiteContentContext";
const fireEvent = (name, params = {}) => {
  if (window.gtag) window.gtag("event", name, params);
};
const Footer = ({ lang: langProp } = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const siteContent = useSiteContent();
  const brandLogo = siteContent?.media?.brandLogo || Logo;
  const contentSocials = siteContent?.settings?.socials || {};
  const lang = useMemo(() => {
    if (typeof langProp === "string" && langProp) {
      return normalizeLang(langProp);
    }
    return getLangFromPathname(location?.pathname || "/");
  }, [langProp, location?.pathname]);
  const t = useCallback(
    (key, vars, fallbackText) => {
      const result = translate(key, lang, vars);
      if (result === key && typeof fallbackText === "string") {
        return fallbackText;
      }
      return result;
    },
    [lang]
  );
  const footerExtra = useMemo(
    () => ({
      eyebrow: t("footer.extra.eyebrow"),
      primaryCta: t("footer.extra.primaryCta"),
      pricingCta: t("footer.extra.pricingCta"),
      pricing: t("footer.extra.pricing"),
      performers: t("footer.extra.performers"),
      bands: t("footer.extra.bands"),
      businesses: t("footer.extra.businesses"),
      photographers: t("footer.extra.photographers"),
      logos: t(
        "footer.extra.logos",
        null,
        lang === "en"
          ? "Logo design"
          : lang === "es"
          ? "Diseño de logotipo"
          : "Logon suunnittelu"
      ),
      servicesTitle: t("footer.extra.servicesTitle"),
      socialLead: t("footer.extra.socialLead"),
      brandLead: t("footer.extra.brandLead"),
      bottom: t("footer.extra.bottom"),
    }),
    [t, lang]
  );
  const homePath = useMemo(() => {
    return lang && lang !== "fi" ? `/${lang}` : "/";
  }, [lang]);
  const privacyPath = useMemo(() => {
    return lang && lang !== "fi" ? `/${lang}/tietosuoja` : "/tietosuoja";
  }, [lang]);
  const landingPath = useMemo(() => {
    return lang && lang !== "fi"
      ? `/${lang}/verkkosivujen-hinta`
      : "/verkkosivujen-hinta";
  }, [lang]);
  const musicianLandingPath = useMemo(() => {
    return lang && lang !== "fi"
      ? `/${lang}/verkkosivut-muusikoille`
      : "/verkkosivut-muusikoille";
  }, [lang]);
  const bandLandingPath = useMemo(() => {
    return lang && lang !== "fi"
      ? `/${lang}/verkkosivut-bandeille`
      : "/verkkosivut-bandeille";
  }, [lang]);
  const businessLandingPath = useMemo(() => {
    return lang && lang !== "fi"
      ? `/${lang}/verkkosivut-yrityksille`
      : "/verkkosivut-yrityksille";
  }, [lang]);
  const photographerLandingPath = useMemo(() => {
    return lang && lang !== "fi"
      ? `/${lang}/valokuvaajan-verkkosivut`
      : "/valokuvaajan-verkkosivut";
  }, [lang]);
  const logoDesignLandingPath = useMemo(() => {
    return lang && lang !== "fi"
      ? `/${lang}/logon-suunnittelu`
      : "/logon-suunnittelu";
  }, [lang]);
  const normalizePath = useCallback((path) => {
    if (!path) return "/";
    return path.length > 1 ? path.replace(/\/+$/, "") : path;
  }, []);
  const navigateToTop = useCallback(
    (to) => {
      const target = normalizePath(to);
      const current = normalizePath(location.pathname);
      if (current === target) {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        return;
      }
      navigate(to, { replace: false });
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      });
    },
    [location.pathname, navigate, normalizePath]
  );
  const scrollToSectionWithOffset = useCallback(
    (section, behavior = "smooth") => {
      const target = document.getElementById(section);
      if (!target) {
        return;
      }
      const navbarOffset = 96;
      const targetTop =
        target.getBoundingClientRect().top + window.scrollY - navbarOffset;
      window.scrollTo({
        top: Math.max(targetTop, 0),
        left: 0,
        behavior,
      });
    },
    []
  );
  const handleNavigation = useCallback(
    (section) => {
      fireEvent("footer_navigaatio_klikki", {
        kohde: section,
        lang,
      });
      if (normalizePath(location.pathname) !== normalizePath(homePath)) {
        navigate(homePath, { replace: false });
        setTimeout(() => {
          scrollToSectionWithOffset(section);
        }, 400);
        return;
      }
      scrollToSectionWithOffset(section);
    },
    [
      location.pathname,
      navigate,
      homePath,
      normalizePath,
      lang,
      scrollToSectionWithOffset,
    ]
  );
  const handleLandingNavigation = useCallback(
    (path, target) => {
      fireEvent("footer_landeri_klikki", {
        lang,
        kohde: target,
      });
      navigateToTop(path);
    },
    [lang, navigateToTop]
  );
  const navItems = useMemo(
    () => [
      { to: "intro", label: t("footer.nav.home", null, "Koti") },
      { to: "about", label: t("footer.nav.about", null, "Minusta") },
      { to: "ITTimeline", label: t("footer.nav.timeline", null, "Aikajana") },
      { to: "skills", label: t("footer.nav.skills", null, "Osaaminen") },
      { to: "works", label: t("footer.nav.portfolio", null, "Portfolio") },
      { to: "contact", label: t("footer.nav.contact", null, "Ota yhteyttä") },
    ],
    [t]
  );
  const serviceItems = useMemo(
    () => [
      {
        path: businessLandingPath,
        label: footerExtra.businesses,
        target: "verkkosivut_yrityksille",
      },
      {
        path: musicianLandingPath,
        label: footerExtra.performers,
        target: "verkkosivut_esiintyjille",
      },
      {
        path: bandLandingPath,
        label: footerExtra.bands,
        target: "verkkosivut_bandeille",
      },
      {
        path: photographerLandingPath,
        label: footerExtra.photographers,
        target: "valokuvaajan_verkkosivut",
      },
      {
        path: logoDesignLandingPath,
        label: footerExtra.logos,
        target: "logon_suunnittelu",
      },
      {
        path: landingPath,
        label: footerExtra.pricing,
        target: "verkkosivujen_hinta",
      },
    ],
    [
      businessLandingPath,
      musicianLandingPath,
      bandLandingPath,
      photographerLandingPath,
      logoDesignLandingPath,
      landingPath,
      footerExtra,
    ]
  );
  const socialItems = [
    {
      href: contentSocials.facebook || "https://www.facebook.com/arttustenius.sivut/",
      icon: FaFacebookF,
      label: "Facebook",
    },
    {
      href: contentSocials.instagram || "https://www.instagram.com/arttustenius.sivut/",
      icon: FaInstagram,
      label: "Instagram",
    },
    {
      href: contentSocials.linkedin || "https://www.linkedin.com/in/arttu-stenius/",
      icon: FaLinkedinIn,
      label: "LinkedIn",
    },
  ];
  const footerLinkClass =
    "group inline-flex items-center gap-2 text-sm font-medium text-black/60 transition hover:text-lime-700 dark:text-white/60 dark:hover:text-lime-300";
  return (
    <footer
      role="contentinfo"
      itemScope
      itemType="https://schema.org/WPFooter"
      className="relative mt-0 w-full text-black dark:text-white"
    >
      <div className="mx-auto w-full px-2 pb-8 sm:px-4 md:px-6 lg:px-12 xl:px-16">
        <div className="relative mx-auto w-full max-w-6xl overflow-hidden rounded-[2rem] border border-black/10 bg-white/70 px-5 py-7 shadow-[0_20px_80px_rgba(0,0,0,0.08)] backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.04] dark:shadow-[0_20px_80px_rgba(0,0,0,0.25)] sm:px-7 sm:py-8 lg:px-10 lg:py-10">
          <div className="pointer-events-none absolute inset-0" aria-hidden="true">
            <div className="absolute -left-28 -top-28 h-72 w-72 rounded-full bg-lime-400/10 blur-[110px]" />
            <div className="absolute -bottom-32 right-0 h-80 w-80 rounded-full bg-sky-400/10 blur-[120px]" />
          </div>
          <div className="relative grid gap-9 lg:grid-cols-[1.25fr_0.72fr_0.9fr] lg:gap-12">
            <section>
              <div className="flex items-center gap-3">
                <img
                  src={brandLogo}
                  alt=""
                  aria-hidden="true"
                  className="h-11 w-11 shrink-0 rounded-full object-contain shadow-[0_10px_28px_rgba(0,0,0,0.16)]"
                />
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-lime-700 dark:text-lime-300 sm:text-xs">
                    {footerExtra.eyebrow}
                  </p>
                  <h3
                    className="mt-1 text-2xl font-black tracking-tight text-black dark:text-white sm:text-3xl"
                    itemProp="name"
                  >
                    {t("footer.brand.name", null, "Arttu Stenius")}
                  </h3>
                </div>
              </div>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-black/65 dark:text-white/65 sm:text-base">
                {footerExtra.brandLead}
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={() => handleNavigation("contact")}
                  className="inline-flex w-full items-center justify-center rounded-full bg-lime-400 px-5 py-3 text-sm font-bold text-black shadow-[0_14px_32px_rgba(163,230,53,0.22)] transition hover:-translate-y-0.5 hover:bg-lime-300 sm:w-auto"
                >
                  {footerExtra.primaryCta}
                </button>
                <button
                  type="button"
                  onClick={() => navigateToTop(landingPath)}
                  className="inline-flex w-full items-center justify-center rounded-full border border-black/10 bg-white/55 px-5 py-3 text-sm font-bold text-black transition hover:-translate-y-0.5 hover:bg-white/80 dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:bg-white/10 sm:w-auto"
                >
                  {footerExtra.pricingCta}
                </button>
              </div>
              <p className="mt-5 text-xs text-black/45 dark:text-white/45">
                {t("footer.brand.businessId", null, "Y-tunnus 3214378-6")}
              </p>
            </section>
            <nav aria-label={t("footer.nav.ariaLabel", null, "Sivuston navigaatio")}>
              <h4 className="text-sm font-black uppercase tracking-[0.18em] text-black/80 dark:text-white/80">
                {t("footer.nav.title", null, "Sivusto")}
              </h4>
              <ul className="mt-4 grid grid-cols-2 gap-x-5 gap-y-3 lg:grid-cols-1">
                {navItems.map(({ to, label }) => (
                  <li key={to}>
                    <a
                      href={`${homePath}#${to}`}
                      onClick={(event) => {
                        event.preventDefault();
                        handleNavigation(to);
                      }}
                      className={footerLinkClass}
                    >
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-lime-400 opacity-70 transition group-hover:opacity-100" />
                      <span>{label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
            <section>
              <h4 className="text-sm font-black uppercase tracking-[0.18em] text-black/80 dark:text-white/80">
                {footerExtra.servicesTitle}
              </h4>
              <ul className="mt-4 space-y-3">
                {serviceItems.map(({ path, label, target }) => (
                  <li key={target}>
                    <a
                      href={path}
                      onClick={(event) => {
                        event.preventDefault();
                        handleLandingNavigation(path, target);
                      }}
                      className={footerLinkClass}
                    >
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-lime-400 opacity-70 transition group-hover:opacity-100" />
                      <span>{label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          </div>
          <div className="relative mt-9 flex flex-col gap-5 border-t border-black/10 pt-6 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-black/50 dark:text-white/50">
                {t("footer.social.title", null, "Seuraa minua")}
              </p>
              <p className="mt-1 text-sm text-black/55 dark:text-white/55">
                {footerExtra.socialLead}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {socialItems.map(({ href, icon: Icon, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  aria-label={t(
                    "footer.social.followOn",
                    { platform: label },
                    `Seuraa kanavassa ${label}`
                  )}
                  title={label}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white/60 text-black/70 transition hover:-translate-y-0.5 hover:border-lime-500 hover:text-lime-700 dark:border-white/10 dark:bg-white/5 dark:text-white/75 dark:hover:border-lime-300 dark:hover:text-lime-300"
                  onClick={() =>
                    fireEvent("footer_some_klikki", {
                      kanava: label.toLowerCase(),
                      sijainti: "footer",
                      lang,
                    })
                  }
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>
          <div className="relative mt-6 flex flex-col gap-3 border-t border-black/10 pt-5 text-xs text-black/45 dark:border-white/10 dark:text-white/45 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <span itemProp="copyrightHolder">
                © {new Date().getFullYear()} Arttu Stenius
              </span>{" "}
              <span aria-hidden="true">·</span>{" "}
              {footerExtra.bottom}
            </div>
            <a
              href={privacyPath}
              onClick={(event) => {
                event.preventDefault();
                fireEvent("footer_tietosuoja", { lang });
                navigateToTop(privacyPath);
              }}
              className="font-semibold text-black/55 transition hover:text-lime-700 dark:text-white/55 dark:hover:text-lime-300"
            >
              {t("footer.legal.privacy", null, "Tietosuoja")}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
export default Footer;